import React, { useState } from 'react';
import { Container, Row, Col, Form, Button, Badge, Card } from 'react-bootstrap';
import { Bell, Search, Eye, CheckCircle, XCircle, Users, DollarSign, UserCog, Gift } from 'lucide-react';
import NgoLayout from '../../../../layouts/Ngo/NgoLayout';
import '../../../../assets/css/Ngo/controlpanelCSS/ngoNotification.css';

const NgoNotifications = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilter, setActiveFilter] = useState('All');
  const [notifications, setNotifications] = useState([
    {
      id: 1,
      from: 'Student',
      fromType: 'student',
      message: 'Verification request received',
      time: '2 min ago',
      isRead: false,
      icon: Users,
      actions: ['View Details', 'Verify / Reject']
    },
    {
      id: 2,
      from: 'Donor',
      fromType: 'donor',
      message: 'Level 2 completed – reward pending',
      time: '1 hour ago',
      isRead: true,
      icon: DollarSign,
      actions: ['View Donor', 'Assign Reward']
    },
    {
      id: 3,
      from: 'Admin',
      fromType: 'admin',
      message: 'System update requires review',
      time: 'Today',
      isRead: false,
      icon: UserCog,
      actions: ['View Update']
    },
    {
      id: 4,
      from: 'Student',
      fromType: 'student',
      message: 'New fee assistance request submitted',
      time: '3 hours ago',
      isRead: true,
      icon: Users,
      actions: ['View Details', 'Verify / Reject']
    },
    {
      id: 5,
      from: 'Donor',
      fromType: 'donor',
      message: 'Donation of PKR 50,000 received',
      time: 'Yesterday',
      isRead: false,
      icon: DollarSign,
      actions: ['View Donor', 'Send Thank You']
    },
    {
      id: 6,
      from: 'Student',
      fromType: 'student',
      message: 'Books request approved and delivered',
      time: '2 days ago',
      isRead: true,
      icon: Users,
      actions: ['View Details']
    }
  ]);

  // Filter notifications
  const filteredNotifications = notifications.filter(notif => {
    const matchesSearch = notif.message.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         notif.from.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesFilter = activeFilter === 'All' || notif.fromType === activeFilter.toLowerCase();
    return matchesSearch && matchesFilter;
  });

  // Count unread notifications
  const unreadCount = notifications.filter(n => !n.isRead).length;

  // Handle mark all as read
  const handleMarkAllRead = () => {
    setNotifications(notifications.map(n => ({ ...n, isRead: true })));
  };

  // Handle mark as unread
  const handleMarkUnread = (id) => {
    setNotifications(notifications.map(n => 
      n.id === id ? { ...n, isRead: false } : n
    ));
  };

  // Handle mark as read
  const handleMarkRead = (id) => {
    setNotifications(notifications.map(n => 
      n.id === id ? { ...n, isRead: true } : n
    ));
  };

  // Get badge variant
  const getBadgeVariant = (type) => {
    const variants = {
      student: 'nn-badge-student',
      donor: 'nn-badge-donor',
      admin: 'nn-badge-admin'
    };
    return variants[type] || 'nn-badge-student';
  };

  return (
    <NgoLayout>
      <div className="nn-notifications-container">
        <Container fluid>
          {/* Header Section */}
          <Row className="nn-header-section">
            <Col>
              <div className="nn-header-content">
                <div className="nn-header-left">
                  <Bell size={32} className="nn-header-icon" />
                  <div className="nn-header-text">
                    <h1 className="nn-header-title">Notifications</h1>
                    <p className="nn-header-subtitle">Stay updated with all activities</p>
                  </div>
                </div>
                <Badge bg="danger" className="nn-notification-badge">
                  {unreadCount} New
                </Badge>
              </div>
            </Col>
          </Row>

          {/* Search Section */}
          <Row className="nn-search-section">
            <Col md={8} lg={6}>
              <div className="nn-search-wrapper">
                <Search className="nn-search-icon" size={20} />
                <Form.Control
                  type="text"
                  placeholder="Search notifications..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="nn-search-input"
                />
              </div>
            </Col>
          </Row>

          {/* Filter Tabs */}
          <Row className="nn-filter-section">
            <Col>
              <div className="nn-filter-tabs">
                <Button
                  className={`nn-filter-btn ${activeFilter === 'All' ? 'nn-active' : ''}`}
                  onClick={() => setActiveFilter('All')}
                >
                  All
                </Button>
                <Button
                  className={`nn-filter-btn ${activeFilter === 'Students' ? 'nn-active' : ''}`}
                  onClick={() => setActiveFilter('Students')}
                >
                  <Users size={18} /> Students
                </Button>
                <Button
                  className={`nn-filter-btn ${activeFilter === 'Donors' ? 'nn-active' : ''}`}
                  onClick={() => setActiveFilter('Donors')}
                >
                  <DollarSign size={18} /> Donors
                </Button>
                <Button
                  className={`nn-filter-btn ${activeFilter === 'Admin' ? 'nn-active' : ''}`}
                  onClick={() => setActiveFilter('Admin')}
                >
                  <UserCog size={18} /> Admin
                </Button>
              </div>
            </Col>
          </Row>

          {/* Action Buttons */}
          <Row className="nn-action-section">
            <Col>
              <div className="nn-action-buttons">
                <Button className="nn-action-btn nn-view-all">
                  View All
                </Button>
                <Button 
                  className="nn-action-btn nn-mark-read"
                  onClick={handleMarkAllRead}
                >
                  <CheckCircle size={18} /> Mark All as Read
                </Button>
              </div>
            </Col>
          </Row>

          {/* Notifications List */}
          <Row>
            <Col>
              <div className="nn-notifications-list">
                {filteredNotifications.length > 0 ? (
                  filteredNotifications.map((notification) => {
                    const Icon = notification.icon;
                    return (
                      <Card 
                        key={notification.id} 
                        className={`nn-notification-card ${!notification.isRead ? 'nn-unread' : ''}`}
                      >
                        <Card.Body className="nn-card-body">
                          <div className="nn-card-main">
                            <div className="nn-card-left">
                              {!notification.isRead && (
                                <div className="nn-unread-indicator"></div>
                              )}
                              <div className={`nn-card-icon ${getBadgeVariant(notification.fromType)}`}>
                                <Icon size={24} />
                              </div>
                              <div className="nn-card-content">
                                <div className="nn-card-header-row">
                                  <span className="nn-card-from">
                                    From: <strong>{notification.from}</strong>
                                  </span>
                                  <span className="nn-card-time">{notification.time}</span>
                                </div>
                                <p className="nn-card-message">{notification.message}</p>
                              </div>
                            </div>
                            <div className="nn-card-actions">
                              {notification.actions.map((action, index) => (
                                <Button
                                  key={index}
                                  className="nn-card-action-btn"
                                  onClick={() => {
                                    if (!notification.isRead) handleMarkRead(notification.id);
                                  }}
                                >
                                  {action === 'View Details' && <Eye size={16} />}
                                  {action === 'Verify / Reject' && <CheckCircle size={16} />}
                                  {action === 'View Donor' && <Eye size={16} />}
                                  {action === 'Assign Reward' && <Gift size={16} />}
                                  {action === 'View Update' && <Eye size={16} />}
                                  {action === 'Send Thank You' && <CheckCircle size={16} />}
                                  {action}
                                </Button>
                              ))}
                              {notification.isRead && (
                                <Button
                                  className="nn-card-action-btn nn-mark-unread-btn"
                                  onClick={() => handleMarkUnread(notification.id)}
                                >
                                  Mark as Unread
                                </Button>
                              )}
                            </div>
                          </div>
                        </Card.Body>
                      </Card>
                    );
                  })
                ) : (
                  <div className="nn-empty-state">
                    <Bell size={64} className="nn-empty-icon" />
                    <h3>No notifications found</h3>
                    <p>Try adjusting your search or filters</p>
                  </div>
                )}
              </div>
            </Col>
          </Row>

          {/* Load More Button */}
          {filteredNotifications.length > 0 && (
            <Row className="nn-load-more-section">
              <Col className="text-center">
                <Button className="nn-load-more-btn">
                  Load More Notifications
                </Button>
              </Col>
            </Row>
          )}
        </Container>
      </div>
    </NgoLayout>
  );
};

export default NgoNotifications;